import React, { useState } from 'react';

import { Input } from '../../components/Input';
import { Button } from '../../components/Button';
import { Title } from '../../components/Title';

import { SLoginStatus, SLoginStatusProps } from '../../specific';
import styled, { createGlobalStyle } from 'styled-components';
import { LogoInLine } from '../../components/LogoInLineDark';
import { useMedia } from '../../scripts/hooks/mediaQueryHook';

const STATIC_BASE_URL = import.meta.env.VITE_STATIC_URI;

const Content = styled.div`
  display: grid;
  width: 100%;
  justify-content: center;
  justify-items: center;
  align-items: center;
`;

type BodyStyleProps = { background: string };
const BodyStyle = createGlobalStyle<BodyStyleProps>`
  body {
    background-color: ${(props) => props.theme.pallete.primary.main};
    background-image: url(${(props) => props.background});
    background-repeat: no-repeat;
    background-attachment: fixed;
    background-position: center;
  }
`;

/**
 * Template layout of this page for each screen size supported
 */
const StyledContainer = styled.div`
  display: grid;
  justify-content: center;
  justify-items: center;
  align-items: center;
  gap: 0px 60px;

  grid-template:
    'Logo Logo Email' 60px
    'Logo Logo Password' 60px
    'Logo Logo ConfirmPassword' 60px
    'Logo Logo 2FAPin ' 60px
    '.    .    Button' 60px
    '.    .    Feedback' 75px /
    150px 150px 286px;

  @media (max-width: 900px) {
    grid-template:
      'Logo' 60px
      'Logo ' 60px
      'Email' 60px
      'Password' 60px
      'ConfirmPassword' 60px
      '2FAPin ' 60px
      'Button' 60px
      'Feedback' 75px /
      286px;
  }
`;

const Logo = styled.div`
  width: 300px;
  height: 150px;
  grid-area: Logo;

  @media (max-width: 900px) {
    width: 200px;
    height: 95px;
  }
`;

export type ViewRecoveryPasswordProps = {
  /**
   * Action to really recover the password.
   */
  onRecoveryPassword: (password: string, repeatPassword: string, pin: string) => void;
  /**
   * Called once the user hits "Esqueci meu Pin"
   */
  onRecovery2FA: () => void;
  /**
   * The email that the user used to activate the password recovery option.
   */
  email: string;
  /**
   * Current recovery status.
   */
  status?: SLoginStatusProps['status'];
};

/**
 * RecoveryPassword Complete View
 */
export const ViewRecoveryPassword: React.FC<ViewRecoveryPasswordProps> = ({
  status,
  onRecoveryPassword,
  onRecovery2FA,
  email,
}: ViewRecoveryPasswordProps) => {
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const mobile = useMedia('(max-width:480px)');

  const [pin, setPin] = useState('');

  return (
    <>
      <BodyStyle background={`${STATIC_BASE_URL}/bg.jpg`} />
      <Content>
        <StyledContainer>
          <Logo>
            <LogoInLine pad='none' width={mobile ? '150px' : '300px'} height={mobile ? '95px' : '100px'} area='logo' />
          </Logo>
          <Input
            area='Email'
            type='email'
            value={email}
            placeholder='Email'
            radius='medium'
            size='large'
            icon
            disabled
          />
          <Input
            area='Password'
            type='password'
            value={password}
            onChange={(element) => setPassword(element.target.value)}
            onPressEnter={() => {
              onRecoveryPassword(password, repeatPassword, pin);
            }}
            placeholder='Senha'
            radius='medium'
            size='large'
          />
          <Input
            area='ConfirmPassword'
            type='password'
            value={repeatPassword}
            onChange={(element) => setRepeatPassword(element.target.value)}
            onPressEnter={() => {
              onRecoveryPassword(password, repeatPassword, pin);
            }}
            placeholder='Confirmar senha'
            radius='medium'
            size='large'
          />
          <div
            style={{
              display: 'grid',
              gridArea: '2FAPin',
              gridTemplate: '"Input Recover" max-content / 1fr 1fr',
            }}>
            <Input
              area='Input'
              value={pin}
              size='small'
              halign='center'
              onChange={({ currentTarget: { value } }) => {
                if (/^[0-9]*$/.test(value)) {
                  setPin(value.slice(0, 6));
                }
              }}
              placeholder='2FA Pin'
            />
            <Title
              area='Recover'
              size='xsmall'
              color='white'
              valign='center'
              halign='center'
              value='Esqueci meu Pin'
              onClick={() => onRecovery2FA()}
            />
          </div>
          <Button
            area='Button'
            onClick={() => {
              onRecoveryPassword(password, repeatPassword, pin);
            }}
            color='light'
            size='auto'>
            <Title size='small' color='white' value='Alterar Senha' />
          </Button>
          {status && (
            <SLoginStatus
              area='Feedback'
              padCross={{ horizontal: 'small', vertical: 'none' }}
              status={status}
              size='auto'
            />
          )}
        </StyledContainer>
      </Content>
    </>
  );
};
